import {
  ElementRef,
  HostListener,
  Directive,
  Input,
  NgZone,
  OnDestroy,
  OnChanges,
  AfterContentChecked,
  Output,
  EventEmitter,
  Injectable,
} from '@angular/core';

const MAX_LOOKUP_RETRIES = 3;

@Injectable()
export class WindowRef {
  get nativeWindow(): Window {
    return window;
  }
}

@Directive({
  selector: '[appAutoSize]',
  standalone: true,
  providers: [WindowRef],
})
export class AutosizeDirective implements OnDestroy, OnChanges, AfterContentChecked {
  @Input()
  set minRows(value: number) {
    this.minRowsCount = value;
    if (this.textAreaEl) {
      this.textAreaEl.rows = value;
    }
  }
  @Input()
  set appAutoSize(autosize: boolean) {
    this.autosizeVal = typeof autosize === 'boolean' ? autosize : true;
  }
  private minRowsCount!: number;

  @Input() maxRows!: number;

  @Input() onlyGrow = false;

  @Input() useImportant = false;

  @Output() resized = new EventEmitter<number>();

  private autosizeVal = true;

  private retries = 0;

  private textAreaEl!: HTMLTextAreaElement;

  private oldContent!: string;

  private oldWidth!: number;

  private windowResizeHandler: any; /* eslint-disable-line */

  private destroyed = false;

  @HostListener('input', ['$event.target'])
  onInput(): void {
    this.adjust();
  }

  constructor(
    public element: ElementRef,
    private window: WindowRef,
    private zone: NgZone,
  ) {
    if (this.element.nativeElement.tagName !== 'TEXTAREA') {
      this.findNestedTextArea();
    } else {
      this.textAreaEl = this.element.nativeElement;
      this.textAreaEl.style.overflowY = 'hidden';
      this.onTextAreaFound();
    }
  }

  ngOnDestroy() {
    this.destroyed = true;
    if (this.windowResizeHandler) {
      this.window.nativeWindow.removeEventListener('resize', this.windowResizeHandler, false);
    }
  }

  ngAfterContentChecked() {
    this.adjust();
  }

  ngOnChanges() {
    this.adjust(true);
  }

  findNestedTextArea() {
    this.textAreaEl = this.element.nativeElement.querySelector('TEXTAREA');

    if (!this.textAreaEl && this.element.nativeElement.shadowRoot) {
      this.textAreaEl = this.element.nativeElement.shadowRoot.querySelector('TEXTAREA');
    }

    if (!this.textAreaEl) {
      if (this.retries >= MAX_LOOKUP_RETRIES) {
        console.warn('ngx-autosize: textarea not found');
      } else {
        this.retries++;
        setTimeout(() => {
          this.findNestedTextArea();
        }, 100);
      }
      return;
    }

    this.textAreaEl.style.overflowY = 'hidden';
    this.onTextAreaFound();
  }

  onTextAreaFound() {
    this.addWindowResizeHandler();
    setTimeout(() => {
      this.adjust();
    });
  }

  addWindowResizeHandler() {
    this.windowResizeHandler = debounce(() => {
      this.zone.run(() => {
        this.adjust();
      });
    }, 200);

    this.zone.runOutsideAngular(() => {
      this.window.nativeWindow.addEventListener('resize', this.windowResizeHandler, false);
    });
  }

  adjust(inputsChanged = false): void {
    if (this.checkForInputConditions(inputsChanged)) {
      this.oldContent = this.textAreaEl.value;
      this.oldWidth = this.textAreaEl.offsetWidth;

      const clone = this.textAreaEl.cloneNode(true) as HTMLTextAreaElement;
      const parent = this.textAreaEl.parentNode;
      clone.style.width = this.textAreaEl.offsetWidth + 'px';
      clone.style.visibility = 'hidden';
      clone.style.position = 'absolute';
      clone.textContent = this.textAreaEl.value;

      parent?.appendChild(clone);

      clone.style.overflowY = 'hidden';
      clone.style.height = 'auto';

      let height = clone.scrollHeight;

      const computedStyle = this.window.nativeWindow.getComputedStyle(clone, null);
      height += parseInt(computedStyle.getPropertyValue('border-top-width'));
      height += parseInt(computedStyle.getPropertyValue('border-bottom-width'));

      height += parseInt(computedStyle.getPropertyValue('padding-top'));
      height += parseInt(computedStyle.getPropertyValue('padding-bottom'));

      const oldHeight = this.textAreaEl.offsetHeight;
      const willGrow = height > oldHeight;

      if (this.onlyGrow === false || willGrow) {
        this.setHeight(height, oldHeight);
      }

      parent?.removeChild(clone);
    }
  }

  setHeight(height: number, oldHeight: number) {
    const lineHeight = this._getLineHeight();
    const rowsCount = height / lineHeight;

    if (this.minRowsCount && this.minRowsCount >= rowsCount) {
      height = this.minRowsCount * lineHeight;
    } else if (this.maxRows && this.maxRows <= rowsCount) {
      const maxHeight = this.maxRows * lineHeight;
      height = this.onlyGrow ? Math.max(maxHeight, oldHeight) : maxHeight;
      this.textAreaEl.style.overflowY = 'auto';
    } else {
      this.textAreaEl.style.overflowY = 'hidden';
    }

    const heightStyle = height + 'px';
    const important = this.useImportant ? 'important' : '';

    this.textAreaEl.style.setProperty('height', heightStyle, important);

    this.resized.emit(height);
  }

  checkForInputConditions(inputsChanged: boolean): boolean {
    if (this.autosizeVal && !this.destroyed && this.textAreaEl && this.textAreaEl.parentNode) {
      if (inputsChanged === false && this.textAreaEl.value === this.oldContent && this.textAreaEl.offsetWidth === this.oldWidth) {
        return false;
      }
      return true;
    }
    return false;
  }

  private _getLineHeight() {
    let lineHeight = parseInt(this.textAreaEl.style.lineHeight, 10);
    if (isNaN(lineHeight) && this.window.nativeWindow.getComputedStyle) {
      const styles = this.window.nativeWindow.getComputedStyle(this.textAreaEl);
      lineHeight = parseInt(styles.lineHeight, 10);
    }

    if (isNaN(lineHeight)) {
      const fontSize = this.window.nativeWindow.getComputedStyle(this.textAreaEl, null).getPropertyValue('font-size');
      lineHeight = Math.floor(parseInt(fontSize.replace('px', ''), 10) * 1.5);
    }

    return lineHeight;
  }
}

/* eslint-disable-next-line */
const debounce = (func: { apply: (arg0: any, arg1: any[]) => void }, wait: number, immediate = false) => {
  /* eslint-disable */
  let timeout: any;
  const newLocal = this;
  return function (...args: any[]) {
    const later = function () {
      timeout = undefined;
      if (!immediate) {
        func.apply(newLocal, args);
      }
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) {
      func.apply(newLocal, args);
    }
  };
};
